import { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { createAnswerAudio } from "../lib/answer-audio";
import { createFlashcardSession, dueCount, type Rating } from "../lib/flashcards";
import { markWordReviewed } from "../lib/progress.mjs";
import { useLanguage, useStore } from "../app/useStore";

const RATING_LABELS: [Rating, string][] = [["again", "Again"], ["hard", "Hard"], ["good", "Good"], ["easy", "Easy"]];

/** Reveal-and-rate flashcards over the selected content part: Again, Hard, Good or Easy schedules the next review. */
export function FlashcardsPage({ itemId }: { itemId?: string }) {
  const ctx = useLanguage();
  const store = useStore();
  const { deck, progress: p, definition: d } = ctx;
  const focus = itemId === "focus" ? ctx.focus : null;
  const pool = focus ? deck.filter((w) => focus.ids.includes(w.id)) : deck;
  const records = (p.flashcards ??= {});

  const [session, setSession] = useState(() => createFlashcardSession(pool, records, { dueOnly: itemId === "review" }));
  const [revealed, setRevealed] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const answerAudio = useRef(createAnswerAudio((...args) => ctx.playWord(...args))).current;
  const set = focus?.label || ctx.content?.part.title || "Your vocabulary deck";

  useEffect(() => { ctx.activity("study", itemId === "review" ? "review" : focus ? "focus" : null, set); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // The word clip finishing triggers the example clip.
  useEffect(() => store.onAudioComplete((program) => answerAudio.complete(program)), [store, answerAudio]);
  useEffect(() => () => {
    answerAudio.cancel();
    if (store.player.getState().program?.singleClip) store.player.stop();
  }, [store, answerAudio]);

  const w = session.current();
  const exit = <a className="text-link" href={ctx.href("part")}><Icon name="back" /> Exit study</a>;

  if (!w) {
    return (
      <>
        <header className="focus-header">{exit}<span>{set}</span><span /></header>
        <section className="completion-card">
          <h2>{session.completed() ? "Session complete." : "All caught up."}</h2>
          <p>{session.completed()} cards reviewed. Your exposures and review schedule are saved.</p>
          <button className="button" onClick={() => { setSession(createFlashcardSession(pool, records)); setRevealed(false); }}>Study all words again</button>
        </section>
      </>
    );
  }

  const ps = store.playerState;
  const own = !!ps.program?.singleClip && ps.program.id.startsWith("clip-" + w.id + "-");
  const english = p.prefs.english;
  const rate = (rating: Rating) => {
    answerAudio.cancel();
    if (ps.program?.answerAudioToken) store.player.stop();
    ctx.reviewWord(w);
    markWordReviewed(p, w.id);
    session.rate(rating);
    setRevealed(false);
    ctx.save();
  };
  const toggle = (key: "english" | "reading" | "answerAutoplay", value: boolean) => { p.prefs[key] = value; ctx.save(); };

  return (
    <>
      <header className="focus-header">
        {exit}
        <span>{set}</span>
        <button className="text-link" aria-expanded={optionsOpen} aria-controls="study-options" onClick={() => setOptionsOpen(!optionsOpen)}>Settings</button>
      </header>
      <div className="focus-stage">
        <section className="word-card focus-card">
          <div className="card-top"><span>{session.remaining()} remaining</span><span>{session.completed()} reviewed</span></div>
          <div className="word-face">
            <h2 className="target-word" lang={d.id}>{w.word}</h2>
            {revealed ? (
              <>
                {d.capabilities.reading && p.prefs.reading && w.reading && <p className="reading" lang={d.id}>{w.reading}</p>}
                <p className="meaning">{english ? w.meaning : w.targetDefinition || w.ja}</p>
                <div className="phrase">
                  <p lang={d.id}>{w.phrase}</p>
                  {english && <p className="phrase-translation">{w.translation}</p>}
                </div>
              </>
            ) : <p className="meaning">What does this word mean?</p>}
          </div>
          <div className="focus-audio">
            <button className="text-link" aria-label="Play word" onClick={() => { answerAudio.cancel(); void ctx.playWord(w, false); }}><Icon name="sound" /> Word</button>
            {revealed && <button className="text-link" aria-label="Play example" onClick={() => { answerAudio.cancel(); void ctx.playWord(w, true); }}><Icon name="sound" /> Example</button>}
            <button className="text-link" hidden={!own || !!ps.snapshot?.completed} onClick={() => (ps.playing ? store.player.pause() : void store.player.play())}>{ps.playing ? "Pause audio" : "Resume audio"}</button>
            <span id="study-audio-status" role="status">{own ? (ps.error ? "Audio couldn’t play. Tap Word or Example to retry." : ps.playing ? "Playing…" : "") : ""}</span>
          </div>
          <div className="card-actions flashcard-actions">
            {revealed
              ? RATING_LABELS.map(([rating, label]) => <button key={rating} className={"button" + (rating === "good" ? "" : " secondary")} data-rating={rating} onClick={() => rate(rating)}><span>{label}</span></button>)
              : <button className="button" id="show-answer" onClick={() => { setRevealed(true); answerAudio.reveal(w, p.prefs.answerAutoplay !== false); }}>Show answer</button>}
          </div>
        </section>
      </div>
      <section id="study-options" className="focus-options" hidden={!optionsOpen} aria-label="Study settings">
        <div className="settings-row">
          <label className="toggle-label"><input id="english-toggle" type="checkbox" checked={english} onChange={(e) => toggle("english", e.target.checked)} /> English meanings</label>
          {d.capabilities.reading && <label className="toggle-label"><input id="reading-toggle" type="checkbox" checked={p.prefs.reading} onChange={(e) => toggle("reading", e.target.checked)} /> Show readings</label>}
          <label className="toggle-label"><input id="answer-autoplay" type="checkbox" checked={p.prefs.answerAutoplay !== false} onChange={(e) => toggle("answerAutoplay", e.target.checked)} /> Autoplay answer audio</label>
        </div>
        <div className="focus-links">
          <a href={ctx.href("study")}>All words</a>
          <a href={ctx.href("study", "review")}>Due &amp; new ({dueCount(deck, p.flashcards)})</a>
          <a href={ctx.href("study", "deck")}>Browse words</a>
          <a href={ctx.href("study", "sprint")}>Recognition practice</a>
        </div>
      </section>
    </>
  );
}
